/**
 * V2 Configuration Types
 *
 * Type definitions for the V2 chat widget configuration. Matches admin
 * V2ChatConfiguration and translateV1ToV2 output.
 */

import type { GAConfiguration, InitialButtonType, LauncherDisplay } from '@/domain/configuration';
import { BORDER_RADII } from '@/domain/configuration';
import type { Border, PrivacyDisclaimer } from '@/domain/configurationShared';
import type { V2Launcher } from '@/launchers/types';

export const V2_BORDER_RADII = {
  ...BORDER_RADII,
  XL: 'XL',
  XXL: 'XXL',
} as const;

export type V2BorderRadius = (typeof V2_BORDER_RADII)[keyof typeof V2_BORDER_RADII];

export interface V2Tooltips {
  attachmentIcon?: string;
  chatButton?: string;
  closeWidget?: string;
  closeWindow?: string;
  sendIcon?: string;
}

export interface V2EndChatMessages {
  agentEnded?: string;
  consumerNoReply?: string;
  newSessionStarted?: string;
  timeExpired?: string;
}

const BORDER_RADIUS_PX: Record<V2BorderRadius, string> = {
  NONE: '0px',
  SMALL: '8px',
  LARGE: '16px',
  XL: '24px',
  XXL: '32px',
};

export function borderRadiusToPx(radius: V2BorderRadius | undefined): string | undefined {
  return radius ? BORDER_RADIUS_PX[radius] : undefined;
}

export interface V2Border extends Border {
  radius?: V2BorderRadius;
}

/**
 * The display choices a rule may name, in place of its rule set's own polarity.
 * Absent -- and any value this build does not recognise -- keeps the behavior that
 * predates the field, so an older config and an older widget agree.
 */
export const DISPLAY_RULE_BUTTON = 'BUTTON';
/** The bar on a page a HIDE_ON_MATCH rule matches, for a chat whose default is the button. */
export const DISPLAY_RULE_BAR = 'BAR';

/**
 * What a page shows when its rules leave it to the default: there are none, or none
 * matches under HIDE_ON_MATCH. Stored as the CHAT_BAR launcher's `payload.defaultDisplay`,
 * where only DISPLAY_RULE_BUTTON changes anything; absent is the bar.
 */
export type DefaultDisplay = 'BAR' | 'BUTTON';

export interface V2DisplayRuleContent {
  /** What a matching page shows instead of the polarity: DISPLAY_RULE_BUTTON, DISPLAY_RULE_BAR, or nothing. */
  display?: string;
  matchType: string;
  value: string;
}

export interface V2DisplayRule {
  id: string;
  content: V2DisplayRuleContent;
  displayDelay: number;
  type: string;
}

export interface V2DisplayRules {
  rules: V2DisplayRule[];
  showByDefault: boolean;
}

export interface V2Format {
  date?: string | null;
  time?: string;
}

/**
 * The on-page chat launcher's face. `type` picks text-vs-graphic and `useLogo`
 * picks which graphic, so TEXT wins over a logo — V1's text button is a pill
 * sized by its label with nothing else in it.
 *
 * `useLogo` is deliberately optional, and absent is NOT the same as `false`:
 * both launcher tiers already drew `header.logo` on the Enhanced launcher before
 * this field existed, so an absent key has to keep doing that or every existing
 * Enhanced customer with a logo silently loses it. `resolveLauncherButton` owns
 * that fallback; keep it out of the defaults so `deepMerge` leaves it undefined.
 */
export interface V2LauncherButton {
  text: string;
  type: InitialButtonType;
  useLogo?: boolean;
}

export interface V2Header {
  backgroundColor?: string;
  fontColor?: string;
  headingText?: string;
  logo?: string;
  subheadingText?: string;
  textColor?: string;
}

export interface V2Colors {
  accent?: string;
  /** Send icon hover, minimized widget close (X) button, go-to-end button, add to cart button */
  accentColor?: string;
  alert?: string;
  attachmentBackground?: string;
  brandAccent?: string;
  brandPrimary?: string;
  brandSecondary?: string;
  chatButtonIcon?: string;
  chatLauncher?: string;
  greetingCloseButton?: string;
  /** Link text color */
  linkColor?: string;
  productImageBackgroundColor?: string;
  submitArrow?: string;
}

export interface V2Fonts {
  primary?: string;
  secondary?: string;
}

export interface V2InputChatWindow {
  placeholderText: string;
}

export interface V2Input {
  chatWindow: V2InputChatWindow;
  placeholderTexts: [string, ...string[]];
}

export interface V2ChatMessageConfig {
  message: string;
  title: string;
}

export interface V2Messages {
  chatClosed: V2ChatMessageConfig;
  chatThrottled: V2ChatMessageConfig;
  consumerMessageBackgroundColor?: string;
  typingIndicatorText?: string;
}

export interface V2Throttling {
  autoThrottlingEnabled: boolean;
  autoThrottlingThreshold: number;
  percentageRollout: number;
}

export interface V2Appearance {
  alignment: string;
  border: V2Border;
  colors: V2Colors;
  dropShadow: boolean;
  fonts: V2Fonts;
  format: V2Format;
  header: V2Header;
  input: V2Input;
  launcherButton: V2LauncherButton;
  messages: V2Messages;
  privacyDisclaimer: PrivacyDisclaimer;
  showPoweredByGladly: boolean;
  /**
   * Viewport width (px) below which the host-page push is ignored and the sidepanel
   * overlays instead — reserving 500px would leave too thin a content column to use.
   *
   * Read through and validated here, but nothing consumes it yet: push and the 1024
   * default both arrive with the push seam. Presentation is unaffected either way — the
   * panel is a full-height sidepanel down to the mobile breakpoint regardless.
   */
  sidebarCollapseAt?: number;
  zIndex: number;
}

export interface V2Behavior {
  attachmentsEnabled: boolean;
  displayRules: V2DisplayRules;
  gaConfiguration?: GAConfiguration;
  lng: string;
  provider?: string;
  providerConfig?: Record<string, unknown>;
  throttling: V2Throttling;
}

export interface V2Beacon {
  manifestUrl: string;
}

export const QUICK_ACTION_TYPES = {
  ANSWER: 'ANSWER',
} as const;

/** Sends `label` as the consumer's message, referring the conversation to a public answer. */
export interface AnswerQuickAction {
  id: string;
  label: string;
  payload: { answerId: string };
  type: typeof QUICK_ACTION_TYPES.ANSWER;
}

/** A pill offered above the composer at the start of a chat session. Discriminated on `type`. */
export type QuickAction = AnswerQuickAction;

export interface V2Configuration {
  appearance: V2Appearance;
  beacon?: V2Beacon;
  behavior: V2Behavior;
  /**
   * The chat bar or the button; see LAUNCHER_DISPLAY. Only `Gladly.init`'s
   * `configurationOverride` writes this; the stored configuration does not carry it.
   * It beats the CHAT_BAR launcher's stored `payload.defaultDisplay` (DefaultDisplay)
   * on a page no display rule decides. Absent leaves that stored default in charge.
   */
  display?: LauncherDisplay;
  endChatMessages?: V2EndChatMessages;
  launchers?: V2Launcher[];
  /** When true, render the V2 widget with the legacy (V1) look and copy. */
  legacyUI?: boolean;
  quickActions?: QuickAction[];
  tooltips?: V2Tooltips;
}
